import React, { useEffect, useMemo, useState } from 'react';
import { FaSearch, FaSyncAlt, FaTimes } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/PageHeader';
import api from '../api';

const COLORS = {
  forestGreen: '#0F3D2E',
  deepGreen: '#003C3C',
  heritageGold: '#C9A14A',
  offWhite: '#FFFDF7',
  sand: '#F7F3EA',
  softBorder: '#E6D8BE',
  mutedText: '#746A5B',
  success: '#1F8F4D',
  warning: '#9B6B00',
  danger: '#B54135',
};

const STATUS_OPTIONS = [
  'pending',
  'confirmed',
  'preparing',
  'ready_for_delivery',
  'assigned_to_rider',
  'picked_up',
  'en_route',
  'delivered',
  'completed',
  'cancelled',
];

const formatLabel = (value) =>
  String(value || '-')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const money = (value) => `R${Number(value || 0).toFixed(2)}`;

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const getMode = (order) =>
  formatLabel(order.orderMode || order.deliveryMode || 'delivery');

const extractRestaurants = (response) => {
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const badgeStyle = (status) => {
  const value = String(status || '').toLowerCase();

  if (value === 'cancelled' || value === 'failed') {
    return {
      backgroundColor: '#FCEAE8',
      color: COLORS.danger,
      border: '1px solid #F1B7A7',
    };
  }
  if (['delivered', 'completed', 'success', 'paid'].includes(value)) {
    return {
      backgroundColor: '#E7F6ED',
      color: COLORS.success,
      border: '1px solid #B7E2C5',
    };
  }
  if (['pending', 'preparing', 'confirmed'].includes(value)) {
    return {
      backgroundColor: '#FFF4D8',
      color: COLORS.warning,
      border: '1px solid #E4C36C',
    };
  }
  return {
    backgroundColor: '#E8F0ED',
    color: COLORS.forestGreen,
    border: '1px solid #B8CEC6',
  };
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    today: 0,
    active: 0,
    completed: 0,
    cancelled: 0,
    pendingPayment: 0,
    grossValue: 0,
  });
  const [meta, setMeta] = useState({
    total: 0,
    currentPage: 1,
    lastPage: 1,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    paymentStatus: '',
    mode: '',
    restaurantId: '',
    dateFrom: '',
    dateTo: '',
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const loadRestaurants = async () => {
      try {
        const response = await api.fetchAdminRestaurants();
        setRestaurants(extractRestaurants(response));
      } catch (requestError) {
        console.error('Failed to load restaurant filter:', requestError);
      }
    };

    loadRestaurants();
  }, []);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');

      try {
        const response = await api.fetchAllOrders({
          ...filters,
          page,
          limit: 20,
        });
        if (!active) return;
        setOrders(Array.isArray(response?.data) ? response.data : []);
        setMeta(
          response?.meta || { total: 0, currentPage: page, lastPage: 1 },
        );
        setSummary((current) => ({ ...current, ...(response?.summary || {}) }));
      } catch (requestError) {
        if (!active) return;
        setOrders([]);
        setError(requestError.message || 'Could not load orders.');
      } finally {
        if (active) setLoading(false);
      }
    }, filters.search ? 300 : 0);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [filters, page, refreshKey]);

  const updateFilter = (name, value) => {
    setPage(1);
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const hasFilters = useMemo(
    () => Object.values(filters).some(Boolean),
    [filters],
  );

  const clearFilters = () => {
    setPage(1);
    setFilters({
      search: '',
      status: '',
      paymentStatus: '',
      mode: '',
      restaurantId: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Operations"
        title="Orders"
        subtitle="Monitor every order, payment, fulfilment stage, and assignment."
        action={
          <button
            type="button"
            style={styles.iconButton}
            title="Refresh orders"
            aria-label="Refresh orders"
            onClick={() => setRefreshKey((value) => value + 1)}
          >
            <FaSyncAlt />
          </button>
        }
      />

      <div style={styles.summaryGrid}>
        <SummaryCard label="All orders" value={summary.total} />
        <SummaryCard label="Today" value={summary.today} />
        <SummaryCard label="In progress" value={summary.active} />
        <SummaryCard label="Completed" value={summary.completed} />
        <SummaryCard label="Pending payment" value={summary.pendingPayment} />
        <SummaryCard label="Order value" value={money(summary.grossValue)} />
      </div>

      <section style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h3 style={styles.panelTitle}>Order register</h3>
            <p style={styles.panelSubtitle}>
              {meta.total} order{meta.total === 1 ? '' : 's'} match the current
              view.
            </p>
          </div>
        </div>

        <div style={styles.filters}>
          <label style={styles.searchField}>
            <FaSearch style={styles.searchIcon} />
            <input
              type="search"
              value={filters.search}
              onChange={(event) => updateFilter('search', event.target.value)}
              placeholder="Order ID, customer, restaurant..."
              style={styles.searchInput}
            />
          </label>
          <select
            value={filters.status}
            onChange={(event) => updateFilter('status', event.target.value)}
            style={styles.control}
            aria-label="Order status"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {formatLabel(status)}
              </option>
            ))}
          </select>
          <select
            value={filters.paymentStatus}
            onChange={(event) =>
              updateFilter('paymentStatus', event.target.value)
            }
            style={styles.control}
            aria-label="Payment status"
          >
            <option value="">All payments</option>
            <option value="pending">Pending</option>
            <option value="success">Paid</option>
            <option value="failed">Failed</option>
            <option value="abandoned">Abandoned</option>
          </select>
          <select
            value={filters.mode}
            onChange={(event) => updateFilter('mode', event.target.value)}
            style={styles.control}
            aria-label="Order type"
          >
            <option value="">All order types</option>
            <option value="delivery">Delivery</option>
            <option value="pickup">Pickup</option>
            <option value="dining">Dine-in</option>
          </select>
          <select
            value={filters.restaurantId}
            onChange={(event) =>
              updateFilter('restaurantId', event.target.value)
            }
            style={styles.control}
            aria-label="Restaurant"
          >
            <option value="">All restaurants</option>
            {restaurants.map((restaurant) => (
              <option key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(event) => updateFilter('dateFrom', event.target.value)}
            style={styles.control}
            aria-label="From date"
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(event) => updateFilter('dateTo', event.target.value)}
            style={styles.control}
            aria-label="To date"
          />
          <button
            type="button"
            style={styles.clearButton}
            disabled={!hasFilters}
            onClick={clearFilters}
          >
            Clear filters
          </button>
        </div>

        {error && <div style={styles.errorNotice}>{error}</div>}

        <div style={styles.tableShell}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Order</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Restaurant</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Payment</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Placed</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={styles.emptyCell}>
                    Loading orders...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="8" style={styles.emptyCell}>
                    No orders match these filters.
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr
                    key={order.id}
                    style={styles.clickRow}
                    onClick={() => setSelectedOrder(order)}
                  >
                    <td style={styles.td}>
                      <strong>#{order.id}</strong>
                      <div style={styles.muted}>{order.itemsCount} items</div>
                    </td>
                    <td style={styles.td}>
                      <strong>{order.customer?.name || 'Customer'}</strong>
                      <div style={styles.muted}>
                        {order.customer?.phone || order.customer?.email || '-'}
                      </div>
                    </td>
                    <td style={styles.td}>{order.restaurant?.name || '-'}</td>
                    <td style={styles.td}>{getMode(order)}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badge,
                          ...badgeStyle(order.status),
                        }}
                      >
                        {formatLabel(order.status)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badge,
                          ...badgeStyle(order.paymentStatus),
                        }}
                      >
                        {formatLabel(order.paymentStatus)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <strong>{money(order.totalPrice)}</strong>
                    </td>
                    <td style={styles.td}>{formatDateTime(order.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.pagination}>
          <span style={styles.pageText}>
            Page {meta.currentPage || page} of {meta.lastPage || 1}
          </span>
          <div style={styles.pageActions}>
            <button
              type="button"
              style={styles.pageButton}
              disabled={page <= 1 || loading}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              style={styles.pageButton}
              disabled={page >= (meta.lastPage || 1) || loading}
              onClick={() =>
                setPage((value) => Math.min(meta.lastPage || 1, value + 1))
              }
            >
              Next
            </button>
          </div>
        </div>
      </section>

      {selectedOrder && (
        <div style={styles.modalOverlay} onClick={() => setSelectedOrder(null)}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <p style={styles.modalEyebrow}>Order #{selectedOrder.id}</p>
                <h3 style={styles.modalTitle}>
                  {selectedOrder.restaurant?.name || 'Order details'}
                </h3>
              </div>
              <button
                type="button"
                style={styles.closeButton}
                title="Close"
                aria-label="Close order details"
                onClick={() => setSelectedOrder(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div style={styles.detailGrid}>
              <span>Customer</span>
              <strong>{selectedOrder.customer?.name || '-'}</strong>
              <span>Contact</span>
              <strong>
                {selectedOrder.customer?.phone ||
                  selectedOrder.customer?.email ||
                  '-'}
              </strong>
              <span>Order type</span>
              <strong>{getMode(selectedOrder)}</strong>
              <span>Order status</span>
              <strong>{formatLabel(selectedOrder.status)}</strong>
              <span>Payment</span>
              <strong>{formatLabel(selectedOrder.paymentStatus)}</strong>
              <span>Rider</span>
              <strong>{selectedOrder.rider?.name || 'Not assigned'}</strong>
              <span>Delivery address</span>
              <strong>{selectedOrder.deliveryAddress || '-'}</strong>
              <span>Placed</span>
              <strong>{formatDateTime(selectedOrder.createdAt)}</strong>
            </div>

            <div style={styles.itemsSection}>
              <h4 style={styles.itemsTitle}>Items</h4>
              {(selectedOrder.items || []).map((item) => (
                <div key={item.id} style={styles.itemRow}>
                  <span>
                    {item.quantity} x {item.name}
                  </span>
                  <strong>{money(Number(item.price) * Number(item.quantity))}</strong>
                </div>
              ))}
              <div style={styles.totalRow}>
                <span>Total</span>
                <strong>{money(selectedOrder.totalPrice)}</strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div style={styles.summaryCard}>
      <span style={styles.summaryLabel}>{label}</span>
      <strong style={styles.summaryValue}>{value}</strong>
    </div>
  );
}

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginBottom: 20,
  },
  summaryCard: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 16,
    minHeight: 92,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  summaryLabel: {
    color: COLORS.mutedText,
    fontWeight: 800,
    fontSize: 13,
  },
  summaryValue: {
    color: COLORS.forestGreen,
    fontSize: 25,
  },
  iconButton: {
    width: 42,
    height: 42,
    display: 'grid',
    placeItems: 'center',
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 6,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    cursor: 'pointer',
  },
  panel: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 18,
    boxShadow: '0 8px 24px rgba(15,61,46,0.08)',
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 14,
  },
  panelTitle: { margin: 0, color: COLORS.forestGreen },
  panelSubtitle: { margin: '5px 0 0', color: COLORS.mutedText },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))',
    gap: 10,
    marginBottom: 14,
  },
  searchField: {
    position: 'relative',
    minWidth: 0,
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: COLORS.mutedText,
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    height: 42,
    padding: '10px 12px 10px 35px',
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 6,
    color: COLORS.deepGreen,
    backgroundColor: '#fff',
  },
  control: {
    width: '100%',
    minWidth: 0,
    height: 42,
    padding: '9px 11px',
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 6,
    color: COLORS.deepGreen,
    backgroundColor: '#fff',
    fontWeight: 700,
  },
  clearButton: {
    height: 42,
    border: 'none',
    borderRadius: 6,
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    fontWeight: 900,
    cursor: 'pointer',
  },
  errorNotice: {
    backgroundColor: '#FCEAE8',
    border: `1px solid ${COLORS.danger}`,
    color: COLORS.danger,
    borderRadius: 6,
    padding: '11px 13px',
    marginBottom: 14,
    fontWeight: 800,
  },
  tableShell: {
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    minWidth: 1050,
    borderCollapse: 'collapse',
  },
  th: {
    backgroundColor: '#F1E7D2',
    color: COLORS.forestGreen,
    textAlign: 'left',
    padding: '13px 14px',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  td: {
    padding: '14px',
    color: COLORS.forestGreen,
    borderTop: `1px solid ${COLORS.softBorder}`,
    verticalAlign: 'top',
  },
  clickRow: { cursor: 'pointer' },
  muted: { color: COLORS.mutedText, fontSize: 12, marginTop: 4 },
  badge: {
    display: 'inline-block',
    borderRadius: 999,
    padding: '5px 9px',
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: 'nowrap',
  },
  emptyCell: {
    padding: 28,
    textAlign: 'center',
    color: COLORS.mutedText,
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 14,
  },
  pageText: { color: COLORS.mutedText, fontWeight: 800 },
  pageActions: { display: 'flex', gap: 8 },
  pageButton: {
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 6,
    backgroundColor: COLORS.sand,
    color: COLORS.forestGreen,
    padding: '9px 13px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    backgroundColor: 'rgba(15, 61, 46, 0.46)',
  },
  modal: {
    width: 'min(680px, 100%)',
    maxHeight: '90vh',
    overflowY: 'auto',
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 22,
    boxShadow: '0 24px 80px rgba(15, 61, 46, 0.24)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 20,
  },
  modalEyebrow: {
    margin: 0,
    color: COLORS.heritageGold,
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
  },
  modalTitle: {
    margin: '4px 0 0',
    color: COLORS.forestGreen,
    fontSize: 24,
  },
  closeButton: {
    width: 36,
    height: 36,
    display: 'grid',
    placeItems: 'center',
    border: 'none',
    borderRadius: 6,
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    cursor: 'pointer',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '140px minmax(0, 1fr)',
    gap: '12px 16px',
    color: COLORS.deepGreen,
  },
  itemsSection: {
    marginTop: 22,
    paddingTop: 18,
    borderTop: `1px solid ${COLORS.softBorder}`,
  },
  itemsTitle: { margin: '0 0 10px', color: COLORS.forestGreen },
  itemRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    padding: '9px 0',
    color: COLORS.deepGreen,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    marginTop: 8,
    paddingTop: 12,
    borderTop: `1px solid ${COLORS.softBorder}`,
    color: COLORS.forestGreen,
    fontSize: 18,
  },
};
